import React, { useState, useMemo, useEffect } from 'react';
import { useMatch, useSearchParams, useOutletContext } from 'react-router-dom';
import classNames from 'classnames';

import { isValidSearchString } from '@sv-support-sdk/utils/src/helpers/search';
import useMounted from '@sv-support-sdk/utils/src/hooks/useMounded';
import Actions from '@sv-support-sdk/ui-components/src/components/ControlLayout/Actions';
import ActionsItem from '@sv-support-sdk/ui-components/src/components/ControlLayout/Actions/ActionsItem';
import List from '@sv-support-sdk/ui-components/src/components/Lists/List';
import Headline from '@sv-support-sdk/ui-components/src/components/Typography/Headline';

import type { OutletContextType } from '../../types/outlet';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  selectLoading,
  selectPagination,
  selectResultCount,
  selectResultList,
  selectSelectedSearchScopes,
} from '../../store/selectors/search';
import { searchActions } from '../../store/slices/search';
import { routes } from '../../routing';
import { searchResultsByValue } from '../../store/actions/search';
import { $l } from '../../locales';
import { userAuthorizedSelector } from '../../store/selectors/user';
import { formResultLink } from '../../helpers/links';
import { DEFAULT_PER_PAGE } from '../../constants/search';
import useMobileDetect from '../../hooks/useMobileDetect';

import PageContentInner from '../../components/Layout/Page/PageContentInner';
import Badge from '../../components/Feedback/Badge';
import LoaderFallback from '../../components/Feedback/Loader/LoaderFallback';
import Checkbox from '../../components/Controls/Checkbox';
import SearchResult from './components/SearchResult';
import Loader from '../../components/Feedback/Loader';
import ContentPlaceholder from '../ContentPlaceholder';

import css from './style.module.scss';
import setQaAttribute from '../../helpers/setQaAttribute';
import { SUPPORT_SEARCH_RESULT_ITEM_TYPE_SELECTION, SUPPORT_SEARCH_RESULTS } from '../../constants/qaAttributes';

interface ITab {
  key: string;
  text: string;
  isActive: boolean;
  isDisabled: boolean;
}

const searchCategoris = [
  { name: $l('SEARCH.GUIDES'), key: 'topic' },
  { name: $l('SEARCH.FAQ'), key: 'faq_topic' },
  { name: $l('SEARCH.RELEASE_NOTES'), key: 'release_note' },
];

const SearchResults = () => {
  const resultCount = useAppSelector(selectResultCount);
  const resultList = useAppSelector(selectResultList);
  const selectedSearchScopes = useAppSelector(selectSelectedSearchScopes);
  const dispatch = useAppDispatch();
  const pagination = useAppSelector(selectPagination);
  const loading = useAppSelector(selectLoading);
  const [searchParams, setSearchParams] = useSearchParams();
  const isSearchRoute = useMatch(routes.search);
  const isAuthorized = useAppSelector(userAuthorizedSelector);
  const {
    scrollToBottom: { isBottom },
  } = useOutletContext<OutletContextType>();
  const isMounted = useMounted();
  const isMobile = useMobileDetect();

  const [isSearchedOnMount, setIsSearchedOnMount] = useState(false);
  const [isResultsLoaded, setIsResultsLoaded] = useState(false);

  const handleTabClick = (tab: string) => {
    const isScopeSelected = selectedSearchScopes.includes(tab);

    const selectedScopes = isScopeSelected
      ? selectedSearchScopes.filter((scope) => scope !== tab)
      : [...selectedSearchScopes, tab];

    //@ts-expect-error - expected error
    setSearchParams({
      q: searchParams.get('q'),
      scope: selectedScopes,
    });

    dispatch(searchActions.setSelectedSearchScope(selectedScopes));
    dispatch(searchActions.setPagination({ currentPage: 1 }));
    dispatch(searchResultsByValue({ skipCountRequest: false }));
  };

  useEffect(() => {
    if (resultList.length) {
      setIsResultsLoaded(true);
    }
  }, [resultList]);

  useEffect(() => {
    if (isBottom && isResultsLoaded && isMounted) {
      dispatch(searchResultsByValue({ skipCountRequest: true, loadMore: true }));
    }
  }, [isBottom, isResultsLoaded, isMounted]);

  useEffect(() => {
    if (!isSearchedOnMount && isSearchRoute) {
      const scope = searchParams.getAll('scope');

      dispatch(searchActions.setPagination({ perPage: DEFAULT_PER_PAGE }));

      if (scope) {
        dispatch(searchActions.setSelectedSearchScope(scope));
      }

      if (!isValidSearchString(searchValueFromParams)) return;

      dispatch(
        searchResultsByValue({ skipCountRequest: false, searchValue: searchValueFromParams }),
      );
      setIsSearchedOnMount(true);
    }
  }, [searchParams, isSearchedOnMount, dispatch, isSearchRoute]);

  useEffect(() => {
    return () => {
      if (isSearchedOnMount) {
        dispatch(searchActions.setValue(''));
      }
    };
  }, [isSearchedOnMount, dispatch]);

  const tabs = useMemo(() => {
    return searchCategoris.reduce<ITab[]>((acc, { name, key }) => {
      const foundTotal = resultCount.find(({ type }) => type === key)?.total || 0;

      acc.push({
        key,
        text: `${name} (${foundTotal})`,
        isActive: selectedSearchScopes.includes(key),
        isDisabled: !foundTotal,
      });

      return acc;
    }, []);
  }, [resultCount, selectedSearchScopes, isAuthorized]);

  const searchValueFromParams = useMemo(() => {
    return searchParams.get('q') || '';
  }, [searchParams]);

  const searchResultsTitle = useMemo(() => {
    return isMobile
      ? $l('SEARCH.SEARCH_RESULTS')
      : `${$l('SEARCH.SEARCH_RESULTS_FOR')} “${searchValueFromParams}”`;
  }, [searchValueFromParams, isMobile]);

  return (
    <PageContentInner height="full" {...setQaAttribute(SUPPORT_SEARCH_RESULTS)}>
      {!isMobile || !loading || resultList.length ? (
        <>
          <Headline direction="row" className="indent-sm-bottom">
            {searchResultsTitle}
            <Badge className="indent-md-left" borderRadius="16" color="grey">
              {pagination.total}
            </Badge>
          </Headline>
          <div
            className={css.results__tabs}
            {...setQaAttribute(SUPPORT_SEARCH_RESULT_ITEM_TYPE_SELECTION)}
          >
            <Actions
              gap={isMobile ? '8' : '16'}
              className="indent-xl-bottom indent-lg-left indent-lg-right"
            >
              {tabs.map(({ text, key, isActive }, index) => (
                <ActionsItem key={key}>
                  <Checkbox
                    isChecked={isActive}
                    text={text}
                    onClick={() => handleTabClick(key)}
                    className={index === tabs.length - 1 ? 'indent-lg-right' : undefined}
                    qaId={`${SUPPORT_SEARCH_RESULT_ITEM_TYPE_SELECTION}-${key}`}
                  />
                </ActionsItem>
              ))}
            </Actions>
          </div>
        </>
      ) : null}
      {!loading && !resultList.length ? (
        <ContentPlaceholder
          icon="96--search"
          title={$l('SEARCH.NOTHING_FOUND')}
          description={$l('SEARCH.TRY_AGAIN')}
          isMobile={isMobile}
        />
      ) : null}
      {resultList.length ? (
        <List
          className={classNames(css.results__list, {
            [css.isMobile]: isMobile,
          })}
        >
          {resultList.map((result, index) => (
            <SearchResult
              key={`${result.type}-${result.slug}`}
              type={result.type}
              description={result.description}
              title={result.title}
              isMobile={isMobile}
              isFirst={index === 0}
              // @ts-expect-error - expected error
              link={formResultLink(result)}
            />
          ))}
        </List>
      ) : null}
      {loading && resultList.length ? (
        <div className={css.results__loader}>
          <Loader />
        </div>
      ) : null}
      {loading && !resultList.length ? <LoaderFallback size={isMobile ? 'md' : undefined} /> : null}
    </PageContentInner>
  );
};

export default SearchResults;
